import { Col, Flex, Image, Row, Tag, Typography, Skeleton } from "antd";
import FallBack from "../../../assets/fallback.png";
import * as _ from "@antv/util";
import { useEffect, useRef, useState } from "react";
import { Chart } from "@antv/g2";
import useFetch from "../../../hooks/useFetch";
import { backend_url, DATA_REFRESH_RATE } from "../../../configs/configs";

const { Title, Text } = Typography;

const GOOD_STATUS = ["Good", "GOOD", "Online", "ONLINE"];
const DANGER_STATUS = ["Danger", "DANGER"];
const WARNING_STATUS = ["Warning", "WARNING"];
const OFFLINE_STATUS = ["Offline", "OFFLINE", undefined, null];

export default function GeneralTab({ themeToken, deviceData, deviceID }) {
  const container = _.uniqueId("-");
  const chartRef = useRef(null);

  const [fetchWrapper] = useFetch();

  const [loading, setLoading] = useState(false);

  const [deviceDetails, setDeviceDetails] = useState({});

  const [deviceUsageToday, setDeviceUsageToday] = useState(null);
  const [deviceUsagePastSevenDays, setDeviceUsagePastSevenDays] =
    useState(null);
  const [deviceUsageMonthToDate, setDeviceUsageMonthToDate] = useState(null);
  const [deviceUsagePastThirtyDaysData, setDeviceUsagePastThirtyDaysData] =
    useState([{ date: "", value: 0 }]);

  const numberFormat = { maximumFractionDigits: 0, minimumFractionDigits: 0 };

  /* API to get device energy usage */
  function getDeviceEnergyUsage() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "device_monitoring/getDeviceEnergyUsage",
      body: {
        device_id: deviceID,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceUsageToday(fetch_output.today_usage);
          setDeviceUsagePastSevenDays(fetch_output.past_seven_days_usage);
          setDeviceUsageMonthToDate(fetch_output.month_to_date_usage);
        }
        setLoading(false);
      },
    });
  }

  /* API to get past 30 days device energy usage */
  function getDeviceEnergyUsagePastThirtyDays() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "device_monitoring/getDeviceEnergyUsagePastThirtyDays",
      body: {
        device_id: deviceID,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceUsagePastThirtyDaysData(
            fetch_output.past_30_days_chart_data?.length > 0
              ? fetch_output.past_30_days_chart_data
              : [
                  {
                    date: "",
                    value: null,
                  },
                ]
          );
        }
        setLoading(false);
      },
    });
  }

  /* API to get selected device details */
  function getSelectedDeviceDetails() {
    setLoading(true);
    fetchWrapper({
      endpoint_url: "device_monitoring/getSelectedDeviceDetails",
      body: {
        device_id: deviceID,
      },
      onSuccess: (fetch_output) => {
        if (fetch_output.status === true) {
          setDeviceDetails(fetch_output.device_details[0]);
        }
        setLoading(false);
      },
    });
  }

  useEffect(() => {
    if (deviceID) {
      getDeviceEnergyUsagePastThirtyDays();
      getDeviceEnergyUsage();
      getSelectedDeviceDetails();
    }

    // refresh data every configurable second
    const timer = setInterval(() => {
      getDeviceEnergyUsage();
      getSelectedDeviceDetails();
    }, DATA_REFRESH_RATE * 1000);

    return () => {
      clearInterval(timer);
    };
  }, [deviceID]);

  useEffect(() => {
    const chart = new Chart({
      container: container,
      autoFit: true,
    });

    chart
      .interval()
      .data(deviceUsagePastThirtyDaysData)
      .encode("x", "date")
      .encode("y", "value")
      .axis("y", {
        labelFill: themeToken.colorTextBase,
        labelOpacity: 1,
        title: "kWh",
        titleFill: themeToken.colorTextBase,
      })
      .axis("x", {
        labelFill: themeToken.colorTextBase,
        labelOpacity: 1,
        title: "Date",
        titleFill: themeToken.colorTextBase,
      })
      .scale("x", { paddingOuter: 0, paddingInner: 0.5 })
      .style("radiusTopLeft", 1000)
      .style("radiusTopRight", 1000);

    document.getElementById(container).innerHTML = "";
    chartRef.current = chart;

    chart.render();

    window.onresize = () => {
      chart.render();
    };

    return () => {
      chartRef?.current?.destroy();
    };
  }, [themeToken, deviceUsagePastThirtyDaysData]);

  return (
    <div
      style={{
        maxHeight: "calc(70vh - 50px)",
        paddingTop: 12,
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <Row gutter={[24, 24]} style={{ overflow: "hidden" }}>
        <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={{ flex: "300px" }}>
          <Flex justify="center" align="center">
            <Image
              width={200}
              src={
                deviceData?.imagePath != null
                  ? `${backend_url}site_management/getDeviceImage?Path=${deviceData?.imagePath}`
                  : null
              }
              fallback={FallBack}
              preview={deviceData?.imagePath != null}
            />
          </Flex>
        </Col>
        <Col xs={24} sm={24} md={24} lg={8} xl={8} xxl={6}>
          <Flex vertical gap={12}>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Model
              </Title>
              <Text>
                {deviceData?.deviceBrand} {deviceData?.deviceModel}
              </Text>
            </div>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Area
              </Title>
              <Tag color={themeToken.purple6}>
                <Text style={{ color: "white" }}>{deviceData?.area}</Text>
              </Tag>
            </div>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                SEU
              </Title>
              <Tag color={themeToken.cyan6}>
                <Text style={{ color: "white" }}>{deviceData?.seu}</Text>
              </Tag>
            </div>
          </Flex>
        </Col>
        <Col xs={24} sm={24} md={24} lg={8} xl={8} xxl={6}>
          <Flex vertical gap={12}>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Last Device Configured
              </Title>
              <Text>{deviceDetails.deviceLastUpdate}</Text>
            </div>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Status
              </Title>
              <Tag
                color={
                  GOOD_STATUS.includes(deviceDetails.status)
                    ? themeToken.colorSuccess
                    : WARNING_STATUS.includes(deviceDetails.status)
                    ? themeToken.colorWarning
                    : DANGER_STATUS.includes(deviceDetails.status)
                    ? themeToken.colorError
                    : OFFLINE_STATUS.includes(deviceDetails.status)
                    ? "grey"
                    : themeToken.colorPrimary
                }
              >
                <Text style={{ color: "white" }}>{deviceDetails.status}</Text>
              </Tag>
            </div>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Last Alarm
              </Title>
              <Text>{deviceDetails.alarmLastUpdate}</Text>
            </div>
          </Flex>
        </Col>
        <Col xs={24} sm={24} md={24} lg={8} xl={8} xxl={{ flex: "auto" }}>
          <Flex vertical gap={12}>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Today Usage
              </Title>
              <Text>
                {deviceUsageToday
                  ? deviceUsageToday === "-"
                    ? "-"
                    : deviceUsageToday.toLocaleString("en-US", numberFormat)
                  : deviceUsageToday === 0
                  ? deviceUsageToday
                  : " - "}
                &nbsp;kWh
              </Text>
            </div>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Past 7 Days Usage
              </Title>
              <Text>
                {deviceUsagePastSevenDays
                  ? deviceUsagePastSevenDays === "-"
                    ? "-"
                    : deviceUsagePastSevenDays.toLocaleString(
                        "en-US",
                        numberFormat
                      )
                  : deviceUsagePastSevenDays === 0
                  ? deviceUsagePastSevenDays
                  : " - "}
                &nbsp;kWh
              </Text>
            </div>
            <div>
              <Title level={4} style={{ marginTop: 0 }}>
                Month Up-to-date
              </Title>
              <Text>
                {deviceUsageMonthToDate
                  ? deviceUsageMonthToDate === "-"
                    ? "-"
                    : deviceUsageMonthToDate.toLocaleString(
                        "en-US",
                        numberFormat
                      )
                  : deviceUsageMonthToDate === 0
                  ? deviceUsageMonthToDate
                  : " - "}
                &nbsp;kWh
              </Text>
            </div>
          </Flex>
        </Col>
        <Col span={24}>
          <Title level={4}>Past 30 days Usage</Title>
          <div style={{ height: 280, paddingRight: 24 }} id={container}></div>
        </Col>
      </Row>
    </div>
  );
}
